import React, { useState } from 'react';
import { ArrowLeft, Heart, Phone, Mail, Lock, Eye, EyeOff, User, Loader, Check, Camera, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuthContext } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { AtootbandanLogo } from './AtootbandanLogo';
import { GoogleSignInButton } from './GoogleSignInButton';
import axiosInstance from '../utils/axios.config';
import { BACKEND_CONFIG } from '../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface NewRegistrationFlowProps {
  onNavigate: (screen: any) => void;
  onComplete: () => void;
}

export default function NewRegistrationFlow({ onNavigate, onComplete }: NewRegistrationFlowProps) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationKey, setVerificationKey] = useState('');
  
  // Step 1: Email & Phone
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Step 2: Phone OTP only (email verification removed)
  const [phoneOtp, setPhoneOtp] = useState('');
  
  // Step 3: User Details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  
  // Step 3: Profile Images (minimum 2 required)
  const [profileImages, setProfileImages] = useState<string[]>([]);
  
  const { loadUser } = useAuthContext(); // Get loadUser from context

  // Convert image to base64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Compress image before upload
  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const maxWidth = 1920;
          const maxHeight = 1920;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Dynamic quality based on file size
          const fileSizeMB = file.size / (1024 * 1024);
          let quality = 0.85;
          if (fileSizeMB > 2) {
            quality = 0.4;
          } else if (fileSizeMB > 1) {
            quality = 0.5;
          } else if (fileSizeMB > 0.5) {
            quality = 0.7;
          }
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Image load failed'));
      };
      reader.onerror = () => reject(new Error('File read failed'));
    });
  };

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // Check if adding more images would exceed limit
    if (profileImages.length + files.length > 6) {
      toast.error('Maximum 6 images allowed');
      return;
    }

    try {
      const newImages: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }

        const compressedFile = await compressImage(file);
        const base64 = await convertToBase64(compressedFile);
        newImages.push(base64);
      }

      setProfileImages([...profileImages, ...newImages]);
      toast.success(`${newImages.length} image(s) uploaded and compressed`);
    } catch (error) {
      toast.error('Failed to process images');
    }
  };

  // Remove image
  const removeImage = (index: number) => {
    setProfileImages(profileImages.filter((_, i) => i !== index));
    toast.success('Image removed');
  };

  // STEP 1: Send OTP to email and phone
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!email || !phone) {
      toast.error('Please enter both email and phone number');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (import.meta.env.DEV) {
        console.log('📧 Sending verification OTPs...');
      }
      
      // Use improved registration endpoints (public, no auth required)
      const response = await axiosInstance.post(`${API_URL}/auth/register/send-otp`, {
        email,
        phone
      });
      
      if (response.data.success) {
        setVerificationKey(response.data.data.verificationKey);
        toast.success('Verification codes sent! Check your email and phone');
        setStep(2);
      } else {
        toast.error(response.data.message || 'Failed to send verification codes');
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Send OTP error:', error);
        console.error('Error response:', error.response?.data);
      }
      
      // Check for network error (backend not accessible)
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        if (import.meta.env.DEV) {
          console.error('🚨 CRITICAL: Backend server not accessible!');
          console.error('Backend URL configured:', API_URL);
        }
        
        toast.error('Unable to connect to server', {
          description: 'Backend server is not accessible. Check environment configuration.'
        });
        return;
      }
      
      // Show the actual error message from backend
      const errorMsg = error.response?.data?.message || error.message || 'Failed to send verification codes';
      toast.error(errorMsg);
      
      // If user already exists, show option to go to login
      if (errorMsg.includes('already exists')) {
        toast.info('Try logging in instead', {
          action: {
            label: 'Go to Login',
            onClick: () => onNavigate('login')
          }
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: Verify Phone OTP only
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phoneOtp) {
      toast.error('Please enter phone verification code');
      return;
    }
    
    if (phoneOtp.length !== 6) {
      toast.error('Verification code must be 6 digits');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (import.meta.env.DEV) {
        console.log('🔍 Verifying Phone OTP...');
      }
      
      // Use improved registration endpoint
      const response = await axiosInstance.post(`${API_URL}/auth/register/verify-otp`, {
        verificationKey,
        phoneOtp
      });
      
      if (response.data.success) {
        toast.success('Phone verified successfully!');
        setStep(3);
      } else {
        toast.error(response.data.message || 'Verification failed');
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Verify OTP error:', error);
      }
      const errorMsg = error.response?.data?.message || error.message || 'Verification failed';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async (type: 'email' | 'phone') => {
    setIsLoading(true);
    
    try {
      if (import.meta.env.DEV) {
        console.log(`📧 Resending ${type} OTP...`);
      }
      
      // Use improved registration resend endpoint
      const response = await axiosInstance.post(`${API_URL}/auth/register/resend-otp`, {
        verificationKey,
        type
      });
      
      if (response.data.success) {
        toast.success(`${type === 'email' ? 'Email' : 'Phone'} verification code resent!`);
      } else {
        toast.error(response.data.message || 'Failed to resend code');
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Resend OTP error:', error);
      }
      const errorMsg = error.response?.data?.message || error.message || 'Failed to resend code';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3: Complete registration
  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!firstName || !lastName || !password || !confirmPassword) {
      toast.error('Please fill all fields');
      return;
    }
    
    if (firstName.length < 2 || lastName.length < 2) {
      toast.error('First and last name must be at least 2 characters');
      return;
    }
    
    // Strong password validation (8+ chars, uppercase, lowercase, number, special character)
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      toast.error('Password must contain uppercase, lowercase, number, and special character');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Photos are now optional - users can upload later
    
    setIsLoading(true);
    
    try {
      
      // Use improved registration complete endpoint
      const response = await axiosInstance.post(`${API_URL}/auth/register/complete`, {
        verificationKey,
        firstName,
        lastName,
        password,
        referralCode
      });
      
      
      if (response.data.success) {
        // Save token to localStorage first
        const token = response.data.data.token;
        const userData = response.data.data.user;
        
        localStorage.setItem('token', token);
        
        toast.success('Account created successfully! Please complete your profile.');
        
        // Wait a moment for token to be saved, then load user
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Load user to update auth context
        try {
          const result = await loadUser();
          
          if (result && result.success) {
            // User loaded successfully - navigate
            onComplete();
          } else {
            // If loadUser failed but token exists, still proceed
            // User will be loaded on next mount
            onComplete();
          }
        } catch (error) {
          // Even if error, token is saved so user can proceed
          onComplete();
        }
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Registration failed';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => onNavigate('home')}
          className="mb-6"
        >
          <ArrowLeft className="size-4 mr-2" />
          Back to Home
        </Button>

        {/* Registration Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <AtootbandanLogo size="md" showText={true} />
          </div>

          <h1 className="text-xl md:text-2xl text-center mb-2">Create Account</h1>
          <p className="text-center text-gray-600 mb-6">Step {step} of 3</p>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-rose-500 to-pink-600 transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          {/* STEP 1: Email & Phone */}
          {step === 1 && (
            <>
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div className="text-center mb-4">
                  <h2 className="text-lg mb-2">Verify Your Contact</h2>
                  <p className="text-sm text-gray-600">We'll send verification codes to confirm it's you</p>
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number (10 digits)</Label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/\D/g, '');
                        setPhone(cleaned);
                      }}
                      placeholder="9876543210"
                      className="pl-10"
                      required
                      disabled={isLoading}
                      maxLength={10}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Enter 10 digits without country code</p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader className="size-4 mr-2 animate-spin" />
                      Sending Codes...
                    </>
                  ) : (
                    'Send Verification Codes'
                  )}
                </Button>
              </form>

              <GoogleSignInButton onSuccess={(data) => {
                if (data.isNewUser || !data.user?.profileCompleted) {
                  onNavigate('complete-profile');
                } else {
                  onNavigate('app');
                }
              }} />
            </>
          )}

          {/* STEP 2: Verify OTPs */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center size-12 rounded-full bg-gray-100 mb-3">
                  <Mail className="size-6 text-gray-500" />
                </div>
                <h2 className="text-lg mb-2">Enter Verification Codes</h2>
                <p className="text-sm text-gray-600">
                  We've sent codes to your email and phone
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  (In development: Check backend console for OTP codes)
                </p>
              </div>

              <div>
                <Label htmlFor="phoneOtp">Phone Verification Code</Label>
                <div className="relative mt-2">
                  <Input
                    id="phoneOtp"
                    type="text"
                    value={phoneOtp}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/\D/g, '');
                      setPhoneOtp(cleaned);
                    }}
                    placeholder="789012"
                    className="text-center text-lg tracking-widest"
                    required
                    disabled={isLoading}
                    maxLength={6}
                  />
                </div>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => handleResendOTP('phone')}
                  disabled={isLoading}
                  className="mt-1 text-xs"
                >
                  Resend Phone Code
                </Button>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader className="size-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Check className="size-4 mr-2" />
                      Verify Codes
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* STEP 3: Complete Registration */}
          {step === 3 && (
            <form onSubmit={handleCompleteRegistration} className="space-y-4">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center size-12 rounded-full bg-gray-100 mb-3">
                  <Check className="size-6 text-gray-500" />
                </div>
                <h2 className="text-lg mb-2">Complete Your Profile</h2>
                <p className="text-sm text-gray-600">Almost there! Just a few more details</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="pl-10"
                      required
                      disabled={isLoading}
                      minLength={2}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="pl-10"
                      required
                      disabled={isLoading}
                      minLength={2}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    disabled={isLoading}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    disabled={isLoading}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="referralCode">Referral Code (Optional)</Label>
                <div className="relative mt-2">
                  <Heart className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
                  <Input
                    id="referralCode"
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Enter referral code"
                    className="pl-10 uppercase"
                    disabled={isLoading}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Get special discount on your first plan purchase</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">✅ Email & Phone Verified</p>
                <p className="text-xs text-blue-600">Your account will be secure and verified</p>
              </div>

              <div className="mt-4">
                <Label htmlFor="profileImages">Upload Profile Images (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">You can upload photos now or add them later from your profile</p>
                <div className="relative mt-2">
                  <Input
                    id="profileImages"
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('profileImages')?.click()}
                    disabled={isLoading}
                  >
                    <Camera className="size-4 mr-2" />
                    Upload Images
                  </Button>
                </div>
                {profileImages.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {profileImages.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Profile ${index + 1}`}
                          className="w-16 h-16 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 size-6 p-0 rounded-full bg-red-500 text-white hover:bg-red-600"
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader className="size-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Login Link */}
          <div className="mt-6 text-center">
            <span className="text-gray-600">Already have an account? </span>
            <button
              onClick={() => onNavigate('login')}
              className="text-rose-500 hover:text-rose-600 hover:underline"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}